/**
 * Readiness Definition Service
 *
 * DB read/write for readiness_definitions.
 * Also handles updating the readiness_definition_id FK on
 * workspaces, degap_cycles, tracks, and prioritization_sessions.
 *
 * Not an authority-transition service — direct insert/upsert is appropriate.
 */

import { getSafeSupabaseClient } from './supabaseSync';
import {
  type ReadinessDefinition,
  type ReadinessDefinitionRow,
  rowToReadinessDefinition,
} from '../types/prioritization/readinessDefinition';

const TABLE = 'readiness_definitions';

// ── Read ──────────────────────────────────────────────────────────────────────

export async function fetchReadinessDefinitions(
  workspaceId: string
): Promise<ReadinessDefinition[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('[readinessDefinitionService] fetchReadinessDefinitions error:', error.message);
    return [];
  }
  return (data ?? []).map((row) => rowToReadinessDefinition(row as ReadinessDefinitionRow));
}

/** The workspace's own definition id (workspaces.readiness_definition_id), or null. */
export async function fetchWorkspaceReadinessDefinitionId(workspaceId: string): Promise<string | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('workspaces')
    .select('readiness_definition_id')
    .eq('id', workspaceId)
    .maybeSingle();
  if (error) {
    console.warn('[readinessDefinitionService] fetchWorkspaceReadinessDefinitionId error:', error.message);
    return null;
  }
  return (data as { readiness_definition_id: string | null } | null)?.readiness_definition_id ?? null;
}

export async function fetchReadinessDefinitionById(
  id: string
): Promise<ReadinessDefinition | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.warn('[readinessDefinitionService] fetchReadinessDefinitionById error:', error.message);
    return null;
  }
  if (!data) return null;
  return rowToReadinessDefinition(data as ReadinessDefinitionRow);
}

// ── Write ─────────────────────────────────────────────────────────────────────

export interface CreateReadinessDefinitionInput {
  workspaceId: string;
  name: string;
  description?: string;
  criteria?: ReadinessDefinition['criteria'];
  currentUserId?: string;
}

export async function createReadinessDefinition(
  input: CreateReadinessDefinitionInput
): Promise<ReadinessDefinition | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      workspace_id: input.workspaceId,
      name: input.name,
      description: input.description ?? null,
      criteria: input.criteria ?? [],
      created_by: input.currentUserId ?? null,
    })
    .select('*')
    .single();

  if (error) {
    console.error('[readinessDefinitionService] createReadinessDefinition error:', error.message);
    return null;
  }
  return rowToReadinessDefinition(data as ReadinessDefinitionRow);
}

export interface UpdateReadinessDefinitionInput {
  id: string;
  name?: string;
  description?: string | null;
  criteria?: ReadinessDefinition['criteria'];
}

export async function updateReadinessDefinition(
  input: UpdateReadinessDefinitionInput
): Promise<ReadinessDefinition | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name;
  if (input.description !== undefined) patch.description = input.description;
  if (input.criteria !== undefined) patch.criteria = input.criteria;

  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();

  if (error) {
    console.error('[readinessDefinitionService] updateReadinessDefinition error:', error.message);
    return null;
  }
  return rowToReadinessDefinition(data as ReadinessDefinitionRow);
}

export async function deleteReadinessDefinition(id: string): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return false;

  // Row security lets only a platform admin delete (migration 379); a refused
  // delete comes back as zero rows, not an error, so count what was deleted.
  const { data, error } = await supabase.from(TABLE).delete().eq('id', id).select('id');
  if (error) {
    console.error('[readinessDefinitionService] deleteReadinessDefinition error:', error.message);
    return false;
  }
  return (data ?? []).length > 0;
}

// ── FK assignment helpers ─────────────────────────────────────────────────────

type Scope = 'workspace' | 'cycle' | 'track' | 'session';

const SCOPE_TABLE: Record<Scope, string> = {
  workspace: 'workspaces',
  cycle: 'degap_cycles',
  track: 'tracks',
  session: 'prioritization_sessions',
};

/**
 * Assign (or clear) the readiness_definition_id FK on a workspace,
 * cycle, track, or session. Pass null to clear the override.
 */
export async function assignReadinessDefinition(
  scope: Scope,
  scopeId: string,
  definitionId: string | null
): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return false;

  const table = SCOPE_TABLE[scope];
  const { error } = await supabase
    .from(table)
    .update({ readiness_definition_id: definitionId })
    .eq('id', scopeId);

  if (error) {
    console.error(`[readinessDefinitionService] assignReadinessDefinition (${scope}) error:`, error.message);
    return false;
  }
  return true;
}
